import { useEffect, useState, type ReactNode } from 'react';
import { App, Badge, Button, DatePicker, Dropdown, Form, Input, Popconfirm, Popover, Segmented, Select, Space, Tooltip, type MenuProps } from 'antd';
import {
  AppstoreOutlined,
  BarsOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  FilterOutlined,
  FlagOutlined,
  FolderOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  UndoOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { BulkUpdateTasksInput, TaskPriority } from '@crm/shared';
import { useAuth } from '../../auth/AuthProvider';
import { activeFilterCount, dueFromDay, EMPTY_FILTERS, SORT_LABEL, type TaskFilters, type TaskSort } from '../../features/tasks/view';
import { useBulkDeleteTasks, useBulkUpdateTasks } from '../../features/tasks/api';
import { useProjectScope } from '../../features/projects/scope';
import { errorMessage } from '../../api/client';
import { plural } from '../../utils/format';
import { fireConfetti } from '../../utils/confetti';
import { priorityOptions, useTaskUsers, UserOption } from './TaskBits';
import { projectLabel } from '../../components/ProjectSelect';

export type TaskView = 'list' | 'board' | 'calendar';
export type TaskStatus = 'open' | 'done' | 'all';

/** View settings remembered per page in this browser. */
export function useStoredState<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage unavailable: the setting lasts for this tab */
    }
  }, [key, value]);
  return [value, setValue] as const;
}

interface ToolbarProps {
  view: TaskView;
  onView: (v: TaskView) => void;
  filters: TaskFilters;
  onFilters: (f: TaskFilters) => void;
  sort: TaskSort;
  onSort: (s: TaskSort) => void;
  status: TaskStatus;
  onStatus: (s: TaskStatus) => void;
  /** hide the assignee filter (My tasks) */
  hideAssignee?: boolean;
  actions?: ReactNode;
}

function FilterPanel({ filters, onFilters, hideAssignee }: Pick<ToolbarProps, 'filters' | 'onFilters' | 'hideAssignee'>) {
  const { user } = useAuth();
  const { list } = useTaskUsers();
  return (
    <Form layout="vertical" style={{ width: 300 }}>
      {!hideAssignee && (
        <Form.Item label="Assignee" style={{ marginBottom: 12 }}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Anyone"
            value={filters.assignees}
            onChange={(assignees) => onFilters({ ...filters, assignees })}
            optionFilterProp="name"
            options={[
              ...(user ? [{ value: user.id, name: `Me ${user.name}`, label: 'Me' }] : []),
              { value: 'unassigned', name: 'Unassigned', label: 'Unassigned' },
              ...list.filter((u) => u.id !== user?.id).map((u) => ({ value: u.id, name: u.name, label: <UserOption user={u} /> })),
            ]}
          />
        </Form.Item>
      )}
      <Form.Item label="Priority" style={{ marginBottom: 12 }}>
        <Select
          mode="multiple"
          allowClear
          placeholder="Any priority"
          value={filters.priorities}
          onChange={(priorities) => onFilters({ ...filters, priorities })}
          options={[...priorityOptions, { value: 'none', label: 'No priority' }]}
        />
      </Form.Item>
      <Form.Item label="Due date" style={{ marginBottom: 12 }}>
        <Select
          value={filters.due}
          onChange={(due) => onFilters({ ...filters, due })}
          options={[
            { value: 'any', label: 'Any time' },
            { value: 'overdue', label: 'Overdue' },
            { value: 'today', label: 'Due today' },
            { value: 'week', label: 'Due in the next 7 days' },
            { value: 'none', label: 'No due date' },
          ]}
        />
      </Form.Item>
      <Button block disabled={!activeFilterCount(filters)} onClick={() => onFilters({ ...EMPTY_FILTERS, q: filters.q })}>
        Clear filters
      </Button>
    </Form>
  );
}

export function TaskToolbar(p: ToolbarProps) {
  const [q, setQ] = useState(p.filters.q);
  // search as you type, a moment after the last key
  useEffect(() => {
    const t = window.setTimeout(() => q !== p.filters.q && p.onFilters({ ...p.filters, q }), 200);
    return () => window.clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const count = activeFilterCount(p.filters);
  return (
    <div className="table-toolbar">
      <div className="toolbar-group">
        <Segmented<TaskView>
          value={p.view}
          onChange={p.onView}
          options={[
            { value: 'list', label: 'List', icon: <BarsOutlined /> },
            { value: 'board', label: 'Board', icon: <AppstoreOutlined /> },
            { value: 'calendar', label: 'Calendar', icon: <CalendarOutlined /> },
          ]}
        />
        <Input allowClear prefix={<SearchOutlined />} placeholder="Search tasks" aria-label="Search tasks" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 240 }} />
      </div>
      <div className="toolbar-group">
        <Select<TaskStatus>
          value={p.status}
          onChange={p.onStatus}
          aria-label="Which tasks"
          style={{ width: 170 }}
          options={[
            { value: 'open', label: 'Incomplete tasks' },
            { value: 'done', label: 'Completed tasks' },
            { value: 'all', label: 'All tasks' },
          ]}
        />
        <Popover trigger="click" placement="bottomRight" content={<FilterPanel filters={p.filters} onFilters={p.onFilters} hideAssignee={p.hideAssignee} />}>
          <Badge count={count} size="small">
            <Button icon={<FilterOutlined />}>Filter</Button>
          </Badge>
        </Popover>
        {p.view !== 'calendar' && (
          <Dropdown
            trigger={['click']}
            menu={{
              selectable: true,
              selectedKeys: [p.sort],
              items: (Object.keys(SORT_LABEL) as TaskSort[]).map((k) => ({ key: k, label: SORT_LABEL[k] })),
              onClick: ({ key }) => p.onSort(key as TaskSort),
            }}
          >
            <Tooltip title={p.sort === 'manual' ? 'Sort (drag tasks to reorder while unsorted)' : 'Dragging is off while sorted'}>
              <Button icon={<SortAscendingOutlined />} type={p.sort === 'manual' ? 'default' : 'primary'} ghost={p.sort !== 'manual'}>
                {p.sort === 'manual' ? 'Sort' : SORT_LABEL[p.sort]}
              </Button>
            </Tooltip>
          </Dropdown>
        )}
        {p.actions}
      </div>
    </div>
  );
}

/** Floating bar for the selected tasks: complete, assign, date, priority, move, delete. */
export function TaskBulkBar({ ids, onClear, sections }: { ids: string[]; onClear: () => void; sections?: { id: string; name: string; projectId: string | null }[] }) {
  const { message } = App.useApp();
  const { list, names } = useTaskUsers();
  const scope = useProjectScope();
  const update = useBulkUpdateTasks(names);
  const remove = useBulkDeleteTasks();
  if (!ids.length) return null;
  const n = plural(ids.length, 'task');
  const apply = (patch: BulkUpdateTasksInput['patch'], done: string) =>
    update.mutate({ ids, patch }, { onSuccess: () => message.success(`${done} ${n}`), onError: (e) => message.error(errorMessage(e)) });

  const assignMenu: MenuProps['items'] = [
    { key: 'none', label: 'Unassigned' },
    ...list.map((u) => ({ key: u.id, label: <UserOption user={u} /> })),
  ];
  const priorityMenu: MenuProps['items'] = [...priorityOptions.map((o) => ({ key: o.value, label: o.label })), { key: 'none', label: 'No priority' }];
  return (
    <div className="task-bulk-bar" role="toolbar" aria-label="Selected tasks">
      <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{n} selected</span>
      <Space size={4} wrap>
        <Button
          type="text"
          icon={<CheckCircleOutlined />}
          onClick={(e) => {
            fireConfetti({ x: e.clientX, y: e.clientY });
            apply({ completed: true }, 'Completed');
          }}
        >
          Complete
        </Button>
        <Button type="text" icon={<UndoOutlined />} onClick={() => apply({ completed: false }, 'Reopened')}>
          Reopen
        </Button>
        <Dropdown trigger={['click']} menu={{ items: assignMenu, onClick: ({ key }) => apply({ assigneeUserId: key === 'none' ? null : key }, 'Reassigned') }}>
          <Button type="text" icon={<UserOutlined />}>Assign</Button>
        </Dropdown>
        <DatePicker
          variant="borderless"
          placeholder="Due date"
          aria-label="Set due date"
          onChange={(d) => apply({ dueAt: dueFromDay(d) }, d ? 'Rescheduled' : 'Cleared the due date of')}
          style={{ width: 130 }}
        />
        <Dropdown trigger={['click']} menu={{ items: priorityMenu, onClick: ({ key }) => apply({ priority: key === 'none' ? null : (key as TaskPriority) }, 'Updated') }}>
          <Button type="text" icon={<FlagOutlined />}>Priority</Button>
        </Dropdown>
        {sections && (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                { key: 'none', label: 'No section' },
                ...sections.map((s) => {
                  const p = scope.enabled && !scope.current ? scope.byId(s.projectId) : undefined;
                  return { key: s.id, label: p ? `${s.name} · ${projectLabel(p)}` : s.name };
                }),
              ],
              onClick: ({ key }) => apply({ sectionId: key === 'none' ? null : key }, 'Moved'),
            }}
          >
            <Button type="text" icon={<BarsOutlined />}>Section</Button>
          </Dropdown>
        )}
        {scope.enabled && (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [{ key: 'none', label: 'No project' }, ...scope.projects.map((p) => ({ key: p.id, label: p.name }))],
              onClick: ({ key }) => apply({ projectId: key === 'none' ? null : key }, 'Moved'),
            }}
          >
            <Button type="text" icon={<FolderOutlined />}>Project</Button>
          </Dropdown>
        )}
        <Popconfirm
          title={`Delete ${n}?`}
          description="Their subtasks, comments and files are deleted too."
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() =>
            remove.mutateAsync(ids).then(
              () => {
                message.success(`Deleted ${n}`);
                onClear();
              },
              (e) => message.error(errorMessage(e)),
            )
          }
        >
          <Button type="text" danger icon={<DeleteOutlined />}>Delete</Button>
        </Popconfirm>
      </Space>
      <Button type="text" icon={<CloseOutlined />} aria-label="Clear selection" onClick={onClear} />
    </div>
  );
}
